import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/profile-components/Navbar";
import Topbar from "../../components/profile-components/Topbar";
import API_BASE_URL from "../../config";
import "../SalesPages.css";
import "../Settings.css";
import "../../styles/Tabs.css";

const Carriers = () => {
  const [coverageTypes, setCoverageTypes] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [selectedCoverageTypeId, setSelectedCoverageTypeId] = useState("");
  const [selectedCarrierIds, setSelectedCarrierIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem("token");

  const selectedCoverageType = useMemo(() => {
    return coverageTypes.find(
      (type) => String(type.id) === String(selectedCoverageTypeId)
    );
  }, [coverageTypes, selectedCoverageTypeId]);

  const selectedCarrierIdSet = useMemo(() => {
    return new Set(selectedCarrierIds.map((id) => Number(id)));
  }, [selectedCarrierIds]);

  const filteredCarriers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    if (!term) return carriers;

    return carriers.filter((carrier) =>
      String(carrier.name || "").toLowerCase().includes(term)
    );
  }, [carriers, searchTerm]);

  const selectedCarriers = useMemo(() => {
    return carriers.filter((carrier) => selectedCarrierIdSet.has(Number(carrier.id)));
  }, [carriers, selectedCarrierIdSet]);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (!selectedCoverageTypeId) return;
    loadSelectedCarriers(selectedCoverageTypeId);
  }, [selectedCoverageTypeId]);

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  const sortByName = (list) => {
    return [...list].sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""))
    );
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);
      clearMessages();

      const [coverageRes, carriersRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/coverage-types`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_BASE_URL}/api/carriers`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      const coverageData = await coverageRes.json().catch(() => ({}));
      const carriersData = await carriersRes.json().catch(() => ({}));

      if (!coverageRes.ok) {
        throw new Error(
          coverageData.error ||
            coverageData.message ||
            "Failed to load coverage types."
        );
      }

      if (!carriersRes.ok) {
        throw new Error(
          carriersData.error ||
            carriersData.message ||
            "Failed to load carriers."
        );
      }

      const cleanCoverageTypes = sortByName(Array.isArray(coverageData) ? coverageData : []);
      const cleanCarriers = sortByName(Array.isArray(carriersData) ? carriersData : []);

      setCoverageTypes(cleanCoverageTypes);
      setCarriers(cleanCarriers);

      if (cleanCoverageTypes.length > 0) {
        setSelectedCoverageTypeId(String(cleanCoverageTypes[0].id));
      }
    } catch (err) {
      console.error("Carrier settings load error:", err);
      setError(err.message || "Failed to load carrier settings.");
    } finally {
      setLoading(false);
    }
  };

  const loadSelectedCarriers = async (coverageTypeId) => {
    try {
      setLoading(true);
      clearMessages();

      const res = await fetch(
        `${API_BASE_URL}/api/subscriber-carriers/coverage-type/${coverageTypeId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          data.error ||
            data.message ||
            "Failed to load selected carriers."
        );
      }

      const ids = Array.isArray(data?.carrierIds)
        ? data.carrierIds
        : Array.isArray(data?.carrier_ids)
          ? data.carrier_ids
          : Array.isArray(data)
            ? data.map((item) => item.carrier_id || item.carrierId || item.id)
            : [];

      setSelectedCarrierIds(
        ids
          .map((id) => Number(id))
          .filter((id) => Number.isInteger(id) && id > 0)
      );
    } catch (err) {
      console.error("Selected carriers load error:", err);
      setError(err.message || "Failed to load selected carriers.");
      setSelectedCarrierIds([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleCarrier = (carrierId) => {
    clearMessages();

    const cleanCarrierId = Number(carrierId);

    setSelectedCarrierIds((prev) => {
      const currentSet = new Set(prev.map((id) => Number(id)));

      if (currentSet.has(cleanCarrierId)) {
        currentSet.delete(cleanCarrierId);
      } else {
        currentSet.add(cleanCarrierId);
      }

      return Array.from(currentSet);
    });
  };

  const handleSelectAllVisible = () => {
    clearMessages();

    setSelectedCarrierIds((prev) => {
      const currentSet = new Set(prev.map((id) => Number(id)));

      filteredCarriers.forEach((carrier) => {
        currentSet.add(Number(carrier.id));
      });

      return Array.from(currentSet);
    });
  };

  const handleClearVisible = () => {
    clearMessages();

    const visibleIds = new Set(filteredCarriers.map((carrier) => Number(carrier.id)));

    setSelectedCarrierIds((prev) =>
      prev.map((id) => Number(id)).filter((id) => !visibleIds.has(id))
    );
  };

  const handleSave = async () => {
    if (!selectedCoverageTypeId) {
      setError("Please choose a coverage type.");
      setSuccess("");
      return;
    }

    try {
      setSaving(true);
      clearMessages();

      const res = await fetch(
        `${API_BASE_URL}/api/subscriber-carriers/coverage-type/${selectedCoverageTypeId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            carrierIds: selectedCarrierIds
          })
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          data.error ||
            data.message ||
            "Failed to save carriers."
        );
      }

      setSuccess("Carriers saved successfully.");
    } catch (err) {
      console.error("Carrier save error:", err);
      setError(err.message || "Failed to save carriers.");
      setSuccess("");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Navbar />
      <Topbar />

      <div className="main-content sales-page settings-page-modern">
        <div className="sales-hero sales-profile-hero">
          <div>
            <p className="sales-eyebrow">Settings</p>
            <h1 className="sales-title">Carriers</h1>
            <p className="sales-subtitle">
              Choose which carriers your agency offers for each coverage type.
            </p>
          </div>

          <div className="sales-header-actions">
            <Link
              className="sales-secondary-btn"
              to="/settings?tab=Data%20%26%20Customization"
              style={{ textDecoration: "none" }}
            >
              ← Back to Data & Customization
            </Link>

            <button
              type="button"
              className="sales-secondary-btn"
              onClick={loadInitialData}
              disabled={loading || saving}
            >
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </div>

        {(error || success) && (
          <div
            className={`sales-section-card ${
              error ? "carrier-message-error" : "carrier-message-success"
            }`}
          >
            {error || success}
          </div>
        )}

        <div className="sales-profile-grid">
          <div className="sales-section-card sales-section-subscription">
            <div className="sales-section-head">
              <h2>Coverage Type</h2>
              <p>Select a coverage type, then choose the carriers your agency offers.</p>
            </div>

            <div className="sales-form-fields">
              <div className="sales-field sales-field-full">
                <label>Coverage Type</label>
                <select
                  value={selectedCoverageTypeId}
                  onChange={(event) => {
                    setSelectedCoverageTypeId(event.target.value);
                    setSearchTerm("");
                    setSelectedCarrierIds([]);
                    clearMessages();
                  }}
                  disabled={loading || saving || coverageTypes.length === 0}
                >
                  {coverageTypes.length === 0 ? (
                    <option value="">No coverage types found</option>
                  ) : (
                    coverageTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))
                  )}
                </select>
              </div>

              <div className="sales-field sales-field-full">
                <label>Search Carriers</label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Search carriers..."
                  disabled={loading || saving || carriers.length === 0}
                />
              </div>
            </div>

            <div className="settings-inline-actions" style={{ marginTop: "16px" }}>
              <button
                type="button"
                className="sales-secondary-btn"
                onClick={handleSelectAllVisible}
                disabled={loading || saving || filteredCarriers.length === 0}
              >
                Select Visible
              </button>

              <button
                type="button"
                className="sales-secondary-btn"
                onClick={handleClearVisible}
                disabled={loading || saving || filteredCarriers.length === 0}
              >
                Clear Visible
              </button>
            </div>

            <p className="settings-muted-text">
              Selected: <strong>{selectedCarrierIds.length}</strong>
              {selectedCoverageType ? ` for ${selectedCoverageType.name}` : ""}
            </p>
          </div>

          <div className="sales-section-card sales-section-lead">
            <div className="sales-section-head">
              <h2>Selected Carriers</h2>
              <p>These carriers will be available for this coverage type in policies and opportunities.</p>
            </div>

            {selectedCarriers.length > 0 ? (
              <div className="settings-inline-actions">
                {selectedCarriers.map((carrier) => (
                  <span key={carrier.id} className="sales-chip sales-chip-outline">
                    {carrier.name}
                  </span>
                ))}
              </div>
            ) : (
              <p className="settings-muted-text">
                No carriers selected for this coverage type yet.
              </p>
            )}

            <div className="settings-inline-actions" style={{ marginTop: "18px" }}>
              <button
                type="button"
                className="sales-primary-btn"
                onClick={handleSave}
                disabled={loading || saving || !selectedCoverageTypeId}
              >
                {saving ? "Saving..." : "Save Carriers"}
              </button>
            </div>
          </div>
        </div>

        <div className="sales-table-card" style={{ marginTop: "12px" }}>
          <div className="sales-table-header">
            <div>
              <h2>Available Carriers</h2>
              <p>
                {selectedCoverageType
                  ? `Choose carriers for ${selectedCoverageType.name}.`
                  : "Choose a coverage type to begin."}
              </p>
            </div>
          </div>

          <div className="sales-table-wrap">
            <table className="sales-table">
              <thead>
                <tr>
                  <th style={{ width: "90px" }}>Selected</th>
                  <th>Carrier</th>
                </tr>
              </thead>
              <tbody>
                {filteredCarriers.length > 0 ? (
                  filteredCarriers.map((carrier) => {
                    const checked = selectedCarrierIdSet.has(Number(carrier.id));

                    return (
                      <tr
                        key={carrier.id}
                        className="sales-click-row"
                        onClick={() => toggleCarrier(carrier.id)}
                      >
                        <td>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleCarrier(carrier.id)}
                            onClick={(event) => event.stopPropagation()}
                            disabled={loading || saving || !selectedCoverageTypeId}
                          />
                        </td>
                        <td>
                          <div className="sales-primary-cell">
                            <span className="sales-name">{carrier.name}</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td className="sales-empty-cell" colSpan="2">
                      {loading ? "Loading carriers..." : "No carriers found."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

export default Carriers;